import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Container,
  Collapse,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  APIgetReport,
  APIgetReportsSettings,
} from '../../api/Reports';

import AdminBreadcrumbs from '../../components/Admin/Breadcrumbs';
import AdminReportsFilterDates from '../../components/Admin/Reports/FilterDates';
import AdminReportsFilterTaxonomies from '../../components/Admin/Reports/FilterTaxonomies';
import AdminReportsFilterUser from '../../components/Admin/Reports/FilterUser';
import AdminReportsButtons from '../../components/Admin/Reports/Buttons';
import AdminPagination from '../../components/Admin/Pagination';

import Loading from '../../components/Loading';

import { reports } from '../../helpers/Reports';

const AdminReport = ({ match: { params: { reportId } } }) => {
  const { intl } = useIntl();
  const [dateFrom, setDateFrom] = useState(moment().subtract(30, 'days'));
  const [dateTo, setDateTo] = useState(moment());
  const [period, setPeriod] = useState('by_day');
  const [terms, setTerms] = useState([]);
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(null);

  const report = reports(intl).find(r => (r.id === reportId));
  const ReportComponent = report.component;
  const iconClass = report.icon || 'fa-file-chart-pie';

  const addTerm = (term) => {
    terms.push(term);
    setTerms(terms);
  };

  const removeTerm = (term) => {
    setTerms(terms.filter(t => (t.value !== term.value)));
  };

  const clearTerms = (taxonomyId) => {
    setTerms(terms.filter(t => (t.taxonomyId !== taxonomyId)));
  };

  const fetchData = (csv = false) => {
    setDataLoading(true);
    return APIgetReport({
      report_id: report.id,
      date_from: report.datePicker === false ? undefined : dateFrom.format('YYYY-MM-DD'),
      date_to: report.datePicker === false ? undefined : dateTo.format('YYYY-MM-DD'),
      terms: terms.length > 0 ? terms.map(t => (t.value)) : undefined,
      period: report.granularity ? period : undefined,
      user_uuid: user ? user.uuid : undefined,
      csv,
      page,
    }).then((response) => {
      if (!csv) {
        setData(response.data);
        setColumns(response.columns);
        setTotalItems(response.total);
      }
      setDataLoading(false);
    });
  };

  useEffect(() => {
    const fetchReportsSettings = () => {
      setSettingsLoading(true);
      APIgetReportsSettings().then((response) => {
        setSettings(response);
        setSettingsLoading(false);
        fetchData();
      });
    };
    fetchReportsSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitView = () => {
    fetchData();
  };

  const handleSubmitDownload = () => {
    fetchData(true);
  };

  useEffect(() => {
    fetchData();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  return (
    <div className="main-content">
      <Container fluid>
        <AdminBreadcrumbs />
        <header className="page-header report-page-header">
          <h2 className="page-title">
            <i className={`far ${iconClass}`} />
            {report.name}
          </h2>
          <div className="report-filters-wrapper form-inline">
            <div className="report-filters-control col-12 mt-3">
              {report.datePicker !== false && (
                <AdminReportsFilterDates
                  settings={settings}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  setDateFrom={setDateFrom}
                  setDateTo={setDateTo}
                  setPeriod={setPeriod}
                  period={period}
                  granularity={report.granularity || false}
                />
              )}
              {report.userPicker && (
                <AdminReportsFilterUser
                  user={user}
                  handleChange={setUser}
                />
              )}
              <AdminReportsButtons
                dataLoading={dataLoading}
                filters={!!report.taxonomiesFilter}
                handleSubmitView={handleSubmitView}
                handleSubmitDownload={handleSubmitDownload}
                handleSwitchFilters={() => { setFiltersOpen(!filtersOpen); }}
              />
            </div>
          </div>
        </header>
        {report.taxonomiesFilter && (
          <Collapse isOpen={filtersOpen} className="report-filters-collapse">
            <AdminReportsFilterTaxonomies
              terms={terms}
              addTerm={addTerm}
              removeTerm={removeTerm}
              clearTerms={clearTerms}
            />
          </Collapse>
        )}
        <div className="content-wrapper">
          {settingsLoading ? (
            <Loading />
          ) : (
            <ReportComponent
              report={report}
              settings={settings}
              data={data}
              terms={terms}
              columns={columns}
              dataLoading={dataLoading}
              dateFrom={dateFrom}
              dateTo={dateTo}
              setDateFrom={setDateFrom}
              setDateTo={setDateTo}
              addTerm={addTerm}
              removeTerm={removeTerm}
              clearTerms={clearTerms}
              fetchData={fetchData}
              handleSubmitView={handleSubmitView}
              handleSubmitDownload={handleSubmitDownload}
            />
          )}
          {(report.pagination && !dataLoading) && (
            <div className="table-footer">
              <AdminPagination
                total={totalItems}
                page={page}
                perPage={20}
                handleChangePage={handleChangePage}
              />
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

AdminReport.propTypes = {
  match: PropTypes.object.isRequired,
};

export default withRouter(AdminReport);
